import { useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import PropTypes from 'prop-types';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useRef, useEffect } from 'react';
import { TranslatorContext } from '@jutro/locale';
import { ContactService } from 'twia-capability-quoteandbind-residential';
import messages from './BuildingAdditionalInterestComponent.messages.js';
import metadata from './BuildingAdditionalInterestComponent.metadata.json5';
import styles from '../BuildingAdditionalInterest.module.scss';
import BuildingAdditionalInterestDetails from '../BuildingAdditionalInterestDetails/BuildingAdditionalInterestDetails.jsx';

import { Button } from '@jutro/legacy/components';

function BuildingAdditionalInterestComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { submissionVM, updateWizardData, showErrors, 
        findInvalidComponent, onValidate, locationId, buildingId, disregardFieldValidation } = props;
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const [additionalInfoVM, setAdditionalInfoVM] = useState();
    const [showAdditionalInterest, setShowAdditionalInterest] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const mailingAddressTypeCode = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.AddressType').getCode('mailingExt');
    const usCountryCode = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.Country').getCode('US');
    const policyContactDetailsDTO = 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO';
    const { refreshData } = useDataRefresh();
    const [additionalInterest, setAdditionalInterest] = useState([]);
    const additionalInterestData = useRef(additionalInterest);
    const translator = useContext(TranslatorContext);
    const selectedUWCompany = _.get(submissionVM, 'baseData.associationName_TWIA.value.code');

    useEffect(() => {
        additionalInterestData.current = _.filter(submissionVM.baseData.policyContactDetails_TWIA?.value,
            pcd => pcd.policyContactRoles[0].subtype === 'PolicyAddlInterest' &&
                pcd.policyContactRoles[0]?.policyAdditionalInterest?.cpAdditionalInterestDetails_TWIA[0]?.buildingId === buildingId)
    }, [])

    const addAdditionalInterest = useCallback(() => {

        const newContact = viewModelService.create({}, 'pc', 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO')

        newContact.active = true;
        newContact.contactType_TWIA.value = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.ContactType').getCode('person');
        newContact.primaryAddress.value = {};
        newContact.primaryAddress.addressType.value = mailingAddressTypeCode
        newContact.primaryAddress.country.value = usCountryCode;

        const pcr = { subtype: 'PolicyAddlInterest' };

        pcr.accountContactRole = { subtype: 'AdditionalInterest' };

        const additionalInterestDetail = { subtype: 'CPBldgAddlInterest', locationId, buildingId, contactType: 'person', filterType: 'CPBldgAddlInterest' };

        newContact.subtype.value = 'Person';

        pcr.policyAdditionalInterest = { cpAdditionalInterestDetails_TWIA: [additionalInterestDetail] };
        newContact.cpAdditionalInterestDetails = additionalInterestDetail;
        newContact.policyContactRoles.value = [pcr];
        setAdditionalInfoVM(() => newContact)
        setShowAdditionalInterest(true)
        // OnNext function will not call again if the changes are not available to the model so setting dummy tag.
        _.set(submissionVM, 'isBuildingAdditionalInterest', true)
        updateWizardData(submissionVM)
    }, [])

    const oldVMData = [];
    const savePremiumFinance = async (updatedData) => {
        const newItem = [...additionalInterestData.current, updatedData.value];
        await saveAdditionalInterest(updatedData)
        updateWizardData(submissionVM);
        setShowAdditionalInterest(false);
    };

    const cancelPremiumFinance = () => {
        setAdditionalInfoVM(oldVMData);
        setShowAdditionalInterest(false);
    };

    const getJobID = (job) => job.quoteID ? job.quoteID.value : job.jobID.value

    const saveAdditionalInterest = async (updatedData) => {

        await ContactService.createPolicyContact(getJobID(submissionVM), updatedData.value, authHeader).then((newContact) => {

            if (newContact) {
                const contactDetailsDTO = viewModelService.create(newContact, 'pc', policyContactDetailsDTO);
                const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

                if (policyContactDetails.value == null) {

                    policyContactDetails.value = {};
                }

                policyContactDetails.value.push(contactDetailsDTO.value);
                additionalInterestData.current.push(contactDetailsDTO.value);
                setShowAdditionalInterest(true);
                updateWizardData(submissionVM)
            }
        })
            .catch(() => {
                showAlert({
                    title: messages.saveFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });

    }


    const deleteAdditionalInterest = async (aiPublicID) => {

        await ContactService.removePolicyContact(getJobID(submissionVM), aiPublicID, authHeader).then(() => {

            const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

            if (policyContactDetails.value != null) {

                policyContactDetails.value = _.filter(policyContactDetails.value, pcd => pcd.publicID !== aiPublicID);
                additionalInterestData.current = additionalInterestData.current.filter((ai) => ai.publicID !== aiPublicID);
                setAdditionalInterest(additionalInterestData.current)
            }
        })
            .catch(() => {
                showAlert({
                    title: messages.deleteFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });

    }

    const deleteRow = async (item) => {

        showConfirm({
            title: messages.removeAdditionalInterest,
            message: messages.removeAddlInterestMessage,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok,
            cancelButtonText: messages.cancel,
            className: styles.jut__Button__filled,
        }).then(async (results) => {

            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }

            const aiPublicID = item.publicID;

            await deleteAdditionalInterest(aiPublicID);
            updateWizardData(submissionVM);

            return true;
        }, _.noop)
    };

    const formatInterestType = (type) => {

        switch (type) {
            case 'CONSALE': return translator(messages.contractofSale);
            case 'LOSSP': return translator(messages.lossPayee);
            case 'MORTGAGEE': return translator(messages.mortgagee);
            default: return type;
        }
    }

    const getName = (item) => item.subtype === 'Person' ? item.firstName + " " + item.lastName : item.contactName;

    const getInterestType = (item) => {
        if (!item && !item.policyContactRoles[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA[0].type) {
            return undefined;
        }

        return formatInterestType(item.policyContactRoles[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA[0].type);
    }

    const getLoanNumber = (item) => item.policyContactRoles[0].policyAdditionalInterest.cpAdditionalInterestDetails_TWIA[0].contractNumber;

    const actionButtons = (item) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                className={styles.buttons}
                type="text"
                icon="gw-delete"
                onClick={() => deleteRow(item)}
                aria-label="Delete Row"
            />
        </div>
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        addPremiumFinance: {
            visible: !showAdditionalInterest,
        },
        additionalInterestSection: {
            visible: showAdditionalInterest,
            item: additionalInfoVM,
            saveData: savePremiumFinance,
            cancelData: cancelPremiumFinance,
            isInvalid: findInvalidComponent,
            showErrors,
            onValidate,
            companyType: selectedUWCompany,
            submissionVM,
            locationId,
            buildingId,
            disregardFieldValidation
        },
        additionalInterestTable: {
            data: additionalInterestData.current
        },
    };

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, `${path}.value`, value);
            updateWizardData(submissionVM);
            refreshData();
        },
        [submissionVM, refreshData, updateWizardData]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: { BuildingAdditionalInterestsDetails: BuildingAdditionalInterestDetails },
        resolveCallbackMap: {
            onAdd: addAdditionalInterest,
            getName,
            getInterestType,
            getLoanNumber,
            actionButtons
        },
    };


    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={submissionVM}
            overrideProps={overrideProps}
            onModelChange={updateWizardData}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValueChange={writeValue}
        />
    );
}

export default BuildingAdditionalInterestComponent;
